import React, { useState, useCallback, useMemo } from 'react';
import { Box, IconButton } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useTranslation } from '../../../app/LanguageContext';
import { isImageMimeOrExtension, isVideoMimeOrExtension } from './mediaMime';
import MediaLightbox from './MediaLightbox';
import { getFileIcon } from './mediaDisplay';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

function toLightboxMedia(item) {
  if (!item) return null;
  return {
    id: item.id,
    mime_type: item.mime_type,
    filename: item.filename || '',
    file_path: item.file_path || item.url?.replace(API_URL, '') || '',
    file_size: item.file_size ?? 0,
  };
}

function HeroMedia({ item, compact, onClick }) {
  if (!item?.url) return null;

  const isImage = isImageMimeOrExtension(item.mime_type, item.filename);
  const isVideo = isVideoMimeOrExtension(item.mime_type, item.filename);
  const heroHeight = compact ? 120 : { xs: 240, sm: 320, md: 360 };

  return (
    <Box
      onClick={onClick}
      sx={{
        width: '100%',
        height: heroHeight,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'action.hover',
        borderRadius: 1,
        overflow: 'hidden',
        cursor: onClick ? 'pointer' : 'default',
      }}
    >
      {isImage ? (
        <Box
          component="img"
          src={item.url}
          alt={item.filename || ''}
          sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
        />
      ) : isVideo ? (
        <Box
          component="video"
          src={item.url}
          muted
          playsInline
          preload="metadata"
          sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
        />
      ) : (
        getFileIcon(item.mime_type, item.filename, compact ? 40 : 56)
      )}
    </Box>
  );
}

export default function ReadOnlyMediaCarousel({
  items = [],
  defaultImageUrl = null,
  compact = false,
  thumbLayout = 'horizontal',
  apiBaseUrl = API_URL,
  desktopOpenContext = null,
}) {
  const { translate } = useTranslation();
  const [activeIndex, setActiveIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const displayItems = useMemo(() => {
    if (items.length > 0) return items;
    if (defaultImageUrl) {
      return [
        {
          id: '__default__',
          url: defaultImageUrl,
          mime_type: 'image/png',
          filename: 'default',
          file_path: '',
        },
      ];
    }
    return [];
  }, [items, defaultImageUrl]);

  const safeIndex = displayItems.length ? Math.min(activeIndex, displayItems.length - 1) : 0;
  const activeItem = displayItems[safeIndex] || null;

  const lightboxItems = useMemo(
    () => displayItems.map(toLightboxMedia).filter((m) => m && m.id !== '__default__'),
    [displayItems],
  );

  const activeLightboxMedia = useMemo(() => {
    if (!activeItem || activeItem.id === '__default__') return null;
    return toLightboxMedia(activeItem);
  }, [activeItem]);

  const goBy = useCallback(
    (delta) => {
      if (displayItems.length <= 1) return;
      setActiveIndex((prev) => {
        const next = prev + delta;
        if (next < 0) return displayItems.length - 1;
        if (next >= displayItems.length) return 0;
        return next;
      });
    },
    [displayItems.length],
  );

  const handleHeroClick = () => {
    if (activeItem?.id === '__default__' || lightboxItems.length === 0) return;
    setLightboxOpen(true);
  };

  if (displayItems.length === 0) {
    return (
      <Box
        sx={{
          height: compact ? 120 : 240,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'action.hover',
          borderRadius: 1,
        }}
      >
        <Box sx={{ color: 'text.disabled', fontSize: compact ? '0.75rem' : '0.875rem' }}>
          {translate('media.no_images')}
        </Box>
      </Box>
    );
  }

  const showNav = displayItems.length > 1;
  const thumbSize = compact ? 44 : 64;

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ position: 'relative' }}>
        <HeroMedia item={activeItem} compact={compact} onClick={handleHeroClick} />
        {showNav && (
          <>
            <IconButton
              size="small"
              aria-label={translate('media.preview_previous')}
              onClick={() => goBy(-1)}
              sx={{
                position: 'absolute',
                left: 4,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: 'background.paper',
                boxShadow: 1,
                '&:hover': { bgcolor: 'background.paper' },
              }}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              aria-label={translate('media.preview_next')}
              onClick={() => goBy(1)}
              sx={{
                position: 'absolute',
                right: 4,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: 'background.paper',
                boxShadow: 1,
                '&:hover': { bgcolor: 'background.paper' },
              }}
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </>
        )}
      </Box>

      {showNav && (
        <Box
          sx={
            thumbLayout === 'grid'
              ? {
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                  gap: 0.75,
                  mt: 1,
                  maxHeight: compact ? 320 : 420,
                  overflowY: 'auto',
                  pr: 0.25,
                }
              : {
                  display: 'flex',
                  gap: 0.75,
                  mt: 1,
                  overflowX: 'auto',
                  pb: 0.5,
                }
          }
        >
          {displayItems.map((item, index) => {
            const isImage = isImageMimeOrExtension(item.mime_type, item.filename);
            const isVideo = isVideoMimeOrExtension(item.mime_type, item.filename);
            const selected = index === safeIndex;

            return (
              <Box
                key={item.id}
                onClick={() => setActiveIndex(index)}
                sx={{
                  flexShrink: thumbLayout === 'grid' ? undefined : 0,
                  width: thumbLayout === 'grid' ? '100%' : thumbSize,
                  height: thumbLayout === 'grid' ? undefined : thumbSize,
                  aspectRatio: thumbLayout === 'grid' ? '1' : undefined,
                  borderRadius: 1,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  border: 2,
                  borderColor: selected ? 'primary.main' : 'divider',
                  bgcolor: 'action.hover',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {isImage ? (
                  <Box
                    component="img"
                    src={item.url}
                    alt=""
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: thumbLayout === 'grid' ? 'contain' : 'cover',
                    }}
                  />
                ) : isVideo ? (
                  <Box
                    component="video"
                    src={item.url}
                    muted
                    playsInline
                    preload="metadata"
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: thumbLayout === 'grid' ? 'contain' : 'cover',
                      pointerEvents: 'none',
                    }}
                  />
                ) : (
                  getFileIcon(item.mime_type, item.filename, 24)
                )}
              </Box>
            );
          })}
        </Box>
      )}

      {lightboxOpen && activeLightboxMedia && lightboxItems.length > 0 ? (
        <MediaLightbox
          open={lightboxOpen}
          onClose={() => setLightboxOpen(false)}
          items={lightboxItems}
          activeMedia={activeLightboxMedia}
          onNavigate={(media) => {
            const idx = lightboxItems.findIndex((m) => m.id === media.id);
            if (idx >= 0) setActiveIndex(idx);
          }}
          apiBaseUrl={apiBaseUrl}
          desktopOpenContext={desktopOpenContext}
        />
      ) : null}
    </Box>
  );
}
